import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { apiURL } from '../../../utilities/URLs'
import { ClickId, company, CustomerContact } from '../../../utilities/CustomerData'
import { useChartConnect } from '@chartapi/react-connect'
import TagManager from 'react-gtm-module'
import { getCompanyGTMID, getGTMDataLayerBody } from '../../../utilities/extensions'

export function ChartConnect({ setReload }) {
  const { contactId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const isReconnect = queryParams.get('isReconnect')

  const [contact, setContact] = useState(new CustomerContact())
  const [clickId, setClickId] = useState(new ClickId())
  const [flowType, setFlowType] = useState('irs-8821-personal')
  const [code, setCode] = useState('')
  const [showError, setShowError] = useState(false)
  const [sendingChartCode, setSendingChartCode] = useState(false)

  const onSuccess = ({ code }) => setCode(code)
  const onError = ({ errorMessage }) => setShowError(true)
  const onClose = () => console.log('User exited Chart Connect')

  const { open } = useChartConnect({
    clientId: process.env.REACT_APP_CHART_CLIENT_ID,
    zIndex: 999,
    sandbox: false,
    flow: flowType,
    onSuccess,
    onError,
    onClose
  })

  TagManager.initialize({
    gtmId: getCompanyGTMID(company.companyId)
  })

  useEffect(() => {
    const handleGetLeadContact = async () => {
      const contactResponse = await fetch(`${apiURL}/api/getLeadContact?contactId=${encodeURIComponent(contactId)}`)
      const contactData = await contactResponse.json()
      if (contactData.response.error)
        console.error(contactData.response.error)
      else {
        const contact = new CustomerContact(contactData.response.contact)
        setClickId(new ClickId(contactData.response.clickId))
        setContact(contact)
        if (isReconnect || !contact.chartAccessToken)
          open()
      }
    }

    document.title = `${company.companyName} | Chart Connect`
    handleGetLeadContact()
  }, [])

  useEffect(() => {
    const handleUploadChartCode = async () => {
      setSendingChartCode(true)

      const chartAccessTokenResponse = await fetch(`${apiURL}/api/uploadChartCode?contactId=${encodeURIComponent(contactId)}&code=${encodeURIComponent(code)}&isAuth=${encodeURIComponent(flowType === 'irs-8821-personal' ? 1 : 0)}&isReconnect=${encodeURIComponent(isReconnect ? 1 : 0)}`)
      const chartAccessTokenData = await chartAccessTokenResponse.json()
      setSendingChartCode(false)

      if (chartAccessTokenData.response.error) {
        console.error(chartAccessTokenData.response.error)
        setShowError(true)
      } else {
        if (flowType === 'irs-8821-personal') {
          setFlowType('full-irs-online-account')
          setCode('')
          if(company.companyId === '1d622c43-baf3-45b6-8225-ec055b0e39f6' || company.companyId === '7bbe1706-8e21-4626-85f4-0a7e7b77acf2')
            TagManager.dataLayer(getGTMDataLayerBody(company.companyId, clickId, 'submitted'))
        } else
          navigate(`/calculateRefund/${chartAccessTokenData.response.jwtToken}`)
      }
    }

    if (code)
      handleUploadChartCode()
  }, [code])

  return (
    <div className='w-screen h-screen flex flex-col items-center justify-center'>
      <img className='w-72 mb-10' src={company.companyLogoURL} />
      <div className='bg-white p-10 rounded-lg shadow-xl max-w-lg max-sm:w-11/12 w-full'>
        {flowType === 'irs-8821-personal' ? (
          <div>
            <p className='font-bold text-3xl text-center text-gray-800 mb-6'>Alright {contact.contactFirstName}, let's connect your IRS documents.</p>
            <div className='flex flex-col space-y-6'>
              <button disabled={!isReconnect && (contact.chartAccessToken || sendingChartCode)} onClick={() => open()} className={`px-10 py-3 text-white font-semibold rounded-lg shadow-md transform transition-all ${!isReconnect && (contact.chartAccessToken || sendingChartCode) ? 'bg-gray-400 cursor-not-allowed' : 'bg-primaryColor hover:bg-primaryDarkColor transition duration-200'}`}>
                Submit Tax Authorization Form
              </button>
              {(contact.chartAccessToken && !isReconnect) && (
                <div className='flex flex-col items-center gap-4'>
                  <p className='text-center'>Your IRS Documents have been requested.</p>
                  <button onClick={() => { setReload(true); navigate('/') }} className='px-10 py-3 text-white font-semibold rounded-lg shadow-md transform bg-primaryColor hover:bg-primaryDarkColor transition duration-200'>
                    Return to Customer Portal
                  </button>
                </div>
              )}
              {sendingChartCode && (
                <div className='flex flex-row items-center justify-center gap-2 -mb-5'>
                  <div className='loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4'></div>
                  <p>Submitting IRS Data...</p>
                </div>
              )}
              {showError && (
                <p className='text-center text-red-600'>Oops! We ran into an issue submitting your IRS information. Please retry again in 10 minutes. Thanks for your patience!</p>
              )}
            </div>
          </div>
        ) : (
          !showError ? (
            <div className='w-full flex flex-col items-center gap-4'>
              <p className='font-bold text-2xl'>Congratulations!</p>
              <p>We have successfully submitted your tax authorization form to the IRS. Here's what happens next:</p>
              <p>To get your calculations <span className='font-bold'>today</span>, click "Login With Your IRS Account in your <span className='text-blue-600 underline'>customer portal</span>. Your request will be processed within minutes and your calculations will be complete.</p>
              <p>If you do not login with your IRS account, this request will take 5-7 business days to process.</p>
              <p>In the meantime, go to your <span className='text-blue-600 underline'>customer portal</span> to stay up to date on your application status, the SETC refund process, and what to expect next.</p>
              <div className='w-full flex flex-col gap-2'>
                <button
                  onClick={() => { setReload(true); navigate('/') }}
                  disabled={sendingChartCode}
                  className={`w-full py-2 text-white rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 max-sm:mb-4 ${sendingChartCode ? 'bg-gray-400 cursor-not-allowed' : 'bg-primaryColor hover:bg-primaryDarkColor transition duration-200'}`}>
                  Go To Customer Portal
                </button>
              </div>
              {sendingChartCode && (
                <div className='flex flex-row items-center justify-center gap-2 -mb-5'>
                  <div className='loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4'></div>
                  <p>Grabbing IRS Data, Please Wait...</p>
                </div>
              )}
            </div>
          ) : (
            <div className='w-full flex flex-col items-center gap-4'>
              <p className='font-bold text-2xl'>Oops!</p>
              <p>We ran into an issue while trying to log in to your IRS account. Please return to your customer portal and try again in about 10 minutes. Thanks for your patience!</p>
              <div className='w-full flex flex-col gap-2'>
                <button
                  onClick={() => { setReload(true); navigate('/') }}
                  className='w-full py-2 text-white rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 max-sm:mb-4 bg-primaryColor hover:bg-primaryDarkColor transition duration-200'>
                  Go To Customer Portal
                </button>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  )
}